import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, RefreshCw, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@/components/ThemeProvider';
import LogoDark from '../assets/logo-dark.svg?url';
import LogoLight from '../assets/logo-light.svg?url';

export default function AuthCallback() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [processingTime, setProcessingTime] = useState(0);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { theme } = useTheme();
  const processingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const sessionCheckTimerRef = useRef<NodeJS.Timeout | null>(null);
  const maxRetryTimeRef = useRef(6);
  
  // Clean up timers on unmount
  useEffect(() => {
    return () => {
      if (processingTimerRef.current) clearInterval(processingTimerRef.current);
      if (sessionCheckTimerRef.current) clearTimeout(sessionCheckTimerRef.current);
    };
  }, []);
  
  // Start a timer to track processing time
  useEffect(() => {
    if (loading) {
      processingTimerRef.current = setInterval(() => {
        setProcessingTime(prev => {
          const newValue = prev + 1;
          
          // After 4 seconds, automatically navigate to dashboard
          if (newValue === 4) {
            sessionStorage.setItem('auth_completed', 'true');
            window.location.replace('/app/dashboard');
          }
          
          return newValue;
        });
      }, 1000);
    } else if (processingTimerRef.current) {
      clearInterval(processingTimerRef.current);
    }
    
    return () => {
      if (processingTimerRef.current) clearInterval(processingTimerRef.current);
    };
  }, [loading]);
  
  // Simple function to navigate to dashboard
  const goToDashboard = () => {
    try {
      // Clear ALL authentication flags to prevent potential loops
      sessionStorage.removeItem('auth_attempt_count');
      sessionStorage.removeItem('auth_in_progress');
      sessionStorage.removeItem('authRedirectPath');
      // Set completed flag to true
      sessionStorage.setItem('auth_completed', 'true');
      
      // Force refresh the session to make sure we're fully synced
      supabase.auth.refreshSession().then(() => {
        // Add a longer delay to ensure state is properly updated
        setTimeout(() => {
          // Use navigate instead of window.location.replace for better state preservation
          navigate('/app/dashboard');
        }, 500);
      }).catch(() => {
        // Fall back to regular navigation even if refresh fails
        setTimeout(() => {
          navigate('/app/dashboard');
        }, 500);
      });
    } catch (error) {
      // Last resort fallback
      navigate('/app/dashboard');
    }
  };

  // Function to manually retry authentication
  const retryAuthentication = async () => {
    setLoading(true);
    setError('');
    setProcessingTime(0);
    
    try {
      // Clear any stale flags before retrying
      sessionStorage.removeItem('auth_attempt_count');
      
      // Force refresh the session first
      const { data: refreshResult, error: refreshError } = await supabase.auth.refreshSession();
      
      if (!refreshError && refreshResult.session) {
        toast.success(t('auth.success.signedIn'));
        // Clear state and proceed to dashboard
        sessionStorage.removeItem('auth_in_progress');
        sessionStorage.setItem('auth_completed', 'true');
        
        await new Promise(resolve => setTimeout(resolve, 200));
        goToDashboard();
        return;
      }
      
      // Fallback to direct session check
      const { data, error: sessionError } = await supabase.auth.getSession();
      
      if (!sessionError && data.session) {
        toast.success(t('auth.success.signedIn'));
        sessionStorage.removeItem('auth_in_progress');
        sessionStorage.setItem('auth_completed', 'true');
        goToDashboard();
        return;
      }
      
      // No session found, directly navigate to dashboard
      sessionStorage.removeItem('auth_in_progress');
      sessionStorage.setItem('auth_completed', 'true');
      window.location.replace('/app/dashboard');
    } catch (err) {
      // Even on error, attempt direct navigation to dashboard
      sessionStorage.removeItem('auth_in_progress');
      sessionStorage.setItem('auth_completed', 'true');
      window.location.replace('/app/dashboard');
    }
  };

  // Function to go back to login
  const goToLogin = () => {
    navigate('/auth/login');
  };

  useEffect(() => {
    // Check if we're already in a potential redirect loop
    const attemptCount = parseInt(sessionStorage.getItem('auth_attempt_count') || '0');
    if (attemptCount > 3) {
      sessionStorage.clear();
      localStorage.removeItem('auth-store');
      // Force reload the page to clear any problematic state
      window.location.href = '/auth/login?reset=true';
      return;
    }
    
    // Increment attempt counter
    sessionStorage.setItem('auth_attempt_count', (attemptCount + 1).toString());

    // Set a global timeout for the entire process
    const globalTimeout = setTimeout(() => {
      if (loading) {
        // Check one last time for a session before showing manual retry
        supabase.auth.getSession().then(({ data: lastSession }) => {
          if (lastSession?.session) {
            sessionStorage.setItem('auth_completed', 'true');
            window.location.replace('/app/dashboard');
          } else {
            setLoading(false);
            setError(t('auth.errors.processingTimeout'));
          }
        }).catch(() => {
          setLoading(false);
          setError(t('auth.errors.processingTimeout'));
        });
      }
    }, maxRetryTimeRef.current * 1000);

    const processCallback = async () => {
      try {
        // Clear any previous flags
        sessionStorage.removeItem('auth_in_progress');
        
        // Check for errors in URL parameters
        const searchParams = new URLSearchParams(window.location.search);
        if (searchParams.get('error')) {
          const errorDescription = searchParams.get('error_description') || 'Authentication failed';
          setError(errorDescription);
          toast.error(t('auth.errors.socialLoginFailed'), {
            description: errorDescription
          });
          setTimeout(() => navigate('/auth/login'), 1000);
          return;
        }
        
        // Check if this is a mobile email confirmation by looking for specific parameters
        const accessToken = searchParams.get('access_token');
        const refreshToken = searchParams.get('refresh_token');
        const type = searchParams.get('type');
        const mobileParam = searchParams.get('mobile');
        
        // If we have auth tokens and this looks like an email confirmation, or mobile parameter is present, check for mobile redirect
        if ((accessToken && refreshToken && type === 'signup') || mobileParam === 'true') {
          // Detect if request came from mobile device
          const userAgent = navigator.userAgent.toLowerCase();
          const isMobile = /android|iphone|ipad|ipod|mobile/.test(userAgent) || mobileParam === 'true';
          
          console.log('📱 Mobile email confirmation detected:', {
            isMobile,
            userAgent: userAgent.substring(0, 50),
            hasTokens: !!accessToken && !!refreshToken,
            type
          });
          
          // Try to redirect to mobile app if this is a mobile device
          if (isMobile) {
            console.log('🔗 Attempting mobile app redirect...');
            
            let finalAccessToken = accessToken;
            let finalRefreshToken = refreshToken;
            
            // If we don't have tokens in URL params, try to get them from the current session
            if (!finalAccessToken || !finalRefreshToken) {
              console.log('⚠️ No tokens in URL, checking current session...');
              const { data: currentSession } = await supabase.auth.getSession();
              if (currentSession.session) {
                finalAccessToken = currentSession.session.access_token;
                finalRefreshToken = currentSession.session.refresh_token;
                console.log('✅ Found tokens in current session');
              }
            }
            
            if (finalAccessToken && finalRefreshToken) {
              // First set the session in Supabase (if not already set)
              const { data: sessionData, error: sessionError } = await supabase.auth.setSession({
                access_token: finalAccessToken,
                refresh_token: finalRefreshToken
              });
              
              if (!sessionError && sessionData.session) {
                console.log('✅ Session set successfully, redirecting to mobile app');
                
                // Try to open the mobile app with the auth tokens
                const mobileUrl = `flowscash://auth/callback?access_token=${finalAccessToken}&refresh_token=${finalRefreshToken}&type=${type || 'signup'}`;
                
                console.log('🔗 Redirecting to mobile app:', mobileUrl);
                
                // Create a link and try to open it
                window.location.href = mobileUrl;
                
                // Show a message to the user
                setLoading(false);
                setError('');
                
                // If the app doesn't open in 3 seconds, show instructions
                setTimeout(() => {
                  if (document.hasFocus()) {
                    // Page still has focus, app probably didn't open
                    console.log('📱 Mobile app redirect may have failed, showing instructions');
                    setError('Please open the Flows.Cash app to complete your email confirmation. If the app did not open automatically, you can open it manually.');
                  }
                }, 3000);
                
                return;
              } else {
                console.log('❌ Failed to set session:', sessionError);
              }
            } else {
              console.log('❌ No auth tokens available for mobile redirect');
              setError('Authentication tokens not found. Please try signing up again.');
              return;
            }
          }
        }
        
        // Continue with normal web auth processing
        // Get the current session directly from Supabase
        const { data, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          setError(t('auth.errors.sessionFailed'));
          toast.error(t('auth.errors.sessionFailed'));
          setTimeout(() => navigate('/auth/login'), 1000);
          return;
        }
        
        if (data.session) {
          // Success! We have a session
          toast.success(t('auth.success.signedIn'));
          
          // Reset the attempt counter now that we've succeeded
          sessionStorage.removeItem('auth_attempt_count');
          
          // Set auth_completed flag but clear auth_in_progress to prevent redirect loops
          sessionStorage.setItem('auth_completed', 'true');
          sessionStorage.removeItem('auth_in_progress');
          
          // Force refresh to ensure auth store is updated with the session
          await supabase.auth.refreshSession();
          
          setTimeout(() => {
            goToDashboard();
          }, 300);
        } else {
          // Try one more time with a shorter delay
          setTimeout(async () => {
            try {
              const { data: retryData, error: retryError } = await supabase.auth.getSession();
              
              if (retryError) {
                throw retryError;
              }
              
              if (retryData.session) {
                toast.success(t('auth.success.signedIn'));
                // Reset the attempt counter
                sessionStorage.removeItem('auth_attempt_count');
                sessionStorage.setItem('auth_completed', 'true');
                sessionStorage.removeItem('auth_in_progress');
                goToDashboard();
              } else {
                // Try direct navigation as Google auth likely worked
                sessionStorage.setItem('auth_completed', 'true');
                window.location.replace('/app/dashboard');
              }
            } catch (retryErr) {
              // Just try direct navigation rather than showing error
              sessionStorage.setItem('auth_completed', 'true');
              window.location.replace('/app/dashboard');
            }
          }, 800);
        }
      } catch (err) {
        // Just try direct navigation rather than showing error
        sessionStorage.setItem('auth_completed', 'true');
        window.location.replace('/app/dashboard');
      }
    };

    processCallback();

    return () => {
      clearTimeout(globalTimeout);
    };
  }, [navigate, t]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background-primary dark:bg-background-dark-primary">
      <div className="w-full max-w-md p-8 space-y-6 bg-background-secondary/80 dark:bg-background-dark-secondary/80 border border-border dark:border-border-dark backdrop-blur-xl backdrop-saturate-150 rounded-xl shadow-sm">
        <div className="flex justify-center">
          <img 
            src={theme === 'dark' ? LogoDark : LogoLight}
            alt="Flows.Cash Logo"
            className="h-12 w-auto"
          />
        </div>
        
        <h1 className="text-center text-2xl font-semibold bg-gradient-to-r from-text-primary to-text-secondary dark:from-text-dark-primary dark:to-text-dark-secondary bg-clip-text text-transparent">
          {loading ? t('auth.authenticating') : error ? t('auth.authenticationFailed') : t('auth.authenticationSuccessful')}
        </h1>
        
        <div className="flex justify-center">
          {loading ? (
            <div className="flex flex-col items-center">
              <Loader2 className="w-10 h-10 text-secondary dark:text-primary-dark animate-spin" />
              <p className="mt-4 text-center text-text-secondary dark:text-text-dark-secondary">
                {t('auth.processingAuthentication')}
              </p>
              {processingTime > 2 && (
                <p className="mt-2 text-sm text-text-secondary/70 dark:text-text-dark-secondary/70">
                  {t('auth.processingTime', { seconds: processingTime })}
                </p>
              )}
              {processingTime > 3 && (
                <div className="mt-4 flex flex-col items-center">
                  <p className="text-sm text-amber-500 dark:text-amber-400 flex items-center">
                    <AlertTriangle className="w-4 h-4 mr-1" />
                    {t('auth.takingLongerThanExpected')}
                  </p>
                  <button
                    onClick={retryAuthentication}
                    className="mt-2 px-3 py-1.5 text-sm flex items-center bg-secondary/10 dark:bg-primary-dark/10 text-secondary dark:text-primary-dark rounded-md hover:bg-secondary/20 dark:hover:bg-primary-dark/20 transition-colors"
                  >
                    <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
                    {t('auth.actions.manualRetry')}
                  </button>
                </div>
              )}
            </div>
          ) : error ? (
            <div className="text-center">
              <p className="text-red-500 dark:text-red-400">
                {error}
              </p>
              <div className="mt-6 flex flex-col gap-3">
                <button
                  onClick={retryAuthentication}
                  className="px-4 py-2 flex items-center justify-center gap-2 bg-secondary text-white rounded-md hover:bg-opacity-90 transition-colors"
                >
                  <RefreshCw className="w-4 h-4" />
                  {t('auth.actions.retryAuthentication')}
                </button>
                <button
                  onClick={goToLogin}
                  className="px-4 py-2 bg-gray-100 dark:bg-gray-800 text-text-primary dark:text-text-dark-primary rounded-md hover:bg-opacity-90 transition-colors"
                >
                  {t('auth.actions.backToLogin')}
                </button>
              </div>
            </div>
          ) : (
            <div className="text-center">
              <p className="text-secondary dark:text-primary-dark text-lg">
                {t('auth.success.accountConnected')}
              </p>
              <p className="mt-4 text-text-secondary dark:text-text-dark-secondary">
                {t('auth.redirectingToDashboard')}
              </p>
              <button
                onClick={goToDashboard}
                className="mt-6 px-4 py-2 bg-primary text-white rounded-md hover:bg-opacity-90 transition-colors"
              >
                {t('auth.actions.goToDashboard')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
} 